import type { KnockGuideFilterParams, KnockGuideStep } from "@knocklabs/client";
import { useGuides } from "@knocklabs/react";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { useHistory } from "react-router-dom";

import { useKnockMarkAsSeen } from "../hooks/useKnockMarkAsSeen";
import { handleGuideAction } from "../lib/handleGuideAction";
import { normalizeButtonContent } from "../lib/normalizeButtonContent";
import { type GuideButtonAction, type GuideButtonContent } from "../types";
import { KnockErrorBoundary } from "./KnockErrorBoundary";
import {
  type BannerContent,
  type KnockGuidesBannerVariant,
  KnockGuidesBannerView,
} from "./KnockGuidesBannerView";

interface KnockBannerContent {
  body: string;
  dismissible?: boolean;
  /* eslint-disable @typescript-eslint/naming-convention */
  primary_button?: GuideButtonContent;
  secondary_button?: GuideButtonContent;
  /* eslint-enable @typescript-eslint/naming-convention */
  title: string;
}

function normalizeContent(content?: KnockBannerContent): BannerContent | undefined {
  const title = content?.title?.trim();
  const body = content?.body?.trim();
  if (!title || !body) {
    return undefined;
  }

  return {
    title,
    body,
    primaryButton: normalizeButtonContent(content?.primary_button),
    secondaryButton: normalizeButtonContent(content?.secondary_button),
    dismissible: content?.dismissible ?? false,
  };
}

export interface KnockGuidesBannerProps {
  filter: KnockGuideFilterParams;
  variant?: KnockGuidesBannerVariant;
  primary?: GuideButtonAction;
  secondary?: GuideButtonAction;
  sx?: SxProps<Theme>;
}

interface BannerItemProps {
  content: BannerContent;
  guideKey: string;
  variant?: KnockGuidesBannerVariant;
  primary?: GuideButtonAction;
  secondary?: GuideButtonAction;
  step: KnockGuideStep;
  sx?: SxProps<Theme>;
}

function BannerItem(props: BannerItemProps) {
  const { content, guideKey, variant, primary, secondary, step, sx } = props;

  const history = useHistory();
  const openUrl = useOpenUrl();
  useKnockMarkAsSeen(step);

  const actionData = {
    key: guideKey,
    navigate: (path: string) => history.push(path),
    openUrl,
    step,
  };

  return (
    <KnockGuidesBannerView
      content={content}
      variant={variant}
      sx={sx}
      onPrimaryClick={
        content.primaryButton
          ? async () => {
              await handleGuideAction({
                ...actionData,
                action: primary,
                content: content.primaryButton!,
              });
            }
          : undefined
      }
      onSecondaryClick={
        content.secondaryButton
          ? async () => {
              await handleGuideAction({
                ...actionData,
                action: secondary,
                content: content.secondaryButton!,
              });
            }
          : undefined
      }
      onDismiss={() => {
        step.markAsArchived();
      }}
    />
  );
}

function KnockGuidesBannerInner(props: KnockGuidesBannerProps) {
  const { filter, variant, primary, secondary, sx } = props;
  const { guides } = useGuides(filter);

  if (guides.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {guides.map((guide) => {
        const step = guide.getStep();
        const content = normalizeContent(step?.content as KnockBannerContent | undefined);

        if (!step || !content) {
          return null;
        }

        return (
          <BannerItem
            key={guide.id}
            content={content}
            guideKey={guide.key}
            variant={variant}
            primary={primary}
            secondary={secondary}
            step={step}
            sx={sx}
          />
        );
      })}
    </Stack>
  );
}

/**
 * Connected Knock guide banner that fetches guides by filter, normalizes
 * Knock content, and renders `KnockGuidesBannerView` for each matching guide.
 *
 * Wrap in a `KnockErrorBoundary` so any Knock SDK errors don't crash the host page.
 */
export function KnockGuidesBanner(props: KnockGuidesBannerProps) {
  return (
    <KnockErrorBoundary>
      <KnockGuidesBannerInner {...props} />
    </KnockErrorBoundary>
  );
}
